<template>
    <base-modal id="edit-chat-panel-modal" :loading="isLoading" size="large" :centered="true">
        <template slot="title"> Edit Chat Interaction </template>
        <template>
            <base-form
                class="mb-4"
                v-model="panel"
                :fields="editPanelFormFields"
                :inline-style="true"
                ref="editPanelForm"
            ></base-form>

            <!--  Chat Agent Selector -->
            <div v-show="chatAgents.length > 0" class="mb-4">
                <div class="d-flex mb-2">
                    <label for="chatAvatar" class="h6 mr-4 mb-0 pt-2">Chat Agent:</label>
                    <div class="flex-grow-1">
                        <select class="form-control mb-2" id="chatAvatar" v-model="panel.chat_agent_id">
                            <option v-if="panel.chat_agent_id === null" :value="null">
                                Select a chat agent...
                            </option>
                            <option v-for="agent in chatAgents" :key="agent.id" :value="agent.id">
                                {{ agent.name }}
                            </option>
                        </select>
                        <a :href="chatAgentsManageRoute" target="_blank" class="pl-1">
                            <span class="align-middle mr-1">Manage Chat Agents</span>
                            <span class="icon icon-glyphicons-basic-square-new-window align-middle"></span>
                        </a>
                    </div>
                </div>
            </div>

            <!-- Empty Chat Agents Alert -->
            <div v-show="chatAgents.length == 0" class="alert alert-info mb-0" role="alert">
                <span class="icon icon-glyphicons-basic-circle-empty-info"></span>
                No chat interaction agents have been created in your workspace yet. Reach out to us at
                <a
                    href="mailto:support@affinitylearning.ca?subject=Support - Avatar Request"
                    target="_blank"
                    class="alert-link"
                    >support@affinitylearning.ca</a
                >
                to learn more about creating a customized chat agent for this scenario.
            </div>

            <!-- Reveal Intents -->
            <div v-show="showIntentConfig">
                <base-form v-model="panel" :fields="chatPanelFormFields" :inline-style="true"></base-form>
                <p class="text-secondary small mb-4">
                    Optionally specify the key pieces of information the learner should ask about or provide during this
                    interaction.
                </p>
                <div v-for="(intent, index) in panel.chat_intents" :key="index">
                    <div v-show="!intent.is_deleted">
                        <!-- Intent Card -->
                        <div class="card my-3 bg-light">
                            <div class="card-body">
                                <div class="d-flex">
                                    <div
                                        class="
                                            flex-grow-0 flex-shrink-0
                                            d-flex
                                            flex-column
                                            align-items-center
                                            mt-1
                                            mr-1
                                        "
                                    >
                                        <div class="mb-2">
                                            <button
                                                type="button"
                                                class="btn btn-transparent py-0 px-1"
                                                @click="removeIntent(intent)"
                                            >
                                                <span class="icon icon-glyphicons-basic-bin"></span>
                                            </button>
                                        </div>
                                        <div class="mb-2">
                                            <div class="btn-group-vertical">
                                                <button
                                                    type="button"
                                                    class="btn btn-transparent btn-sm py-0 px-1"
                                                    @click="shiftIntentIndex(index, -1)"
                                                >
                                                    <span class="icon icon-glyphicons-basic-arrow-up"></span>
                                                </button>
                                                <button
                                                    type="button"
                                                    class="btn btn-transparent btn-sm py-0 px-1"
                                                    @click="shiftIntentIndex(index, 1)"
                                                >
                                                    <span class="icon icon-glyphicons-basic-arrow-down"></span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex-grow-1 px-4">
                                        <div class="row mb-3">
                                            <div class="col-12">
                                                <div class="d-flex align-items-center">
                                                    <div class="flex-grow-0 mr-2">
                                                        <select class="form-control" v-model="intent.detection_type">
                                                            <option value="intent">Question/Statement</option>
                                                            <option value="parameter">Parameter</option>
                                                        </select>
                                                    </div>
                                                    <div class="flex-grow-1">
                                                        <select
                                                            class="form-control"
                                                            v-if="intent.detection_type == 'intent'"
                                                            v-model="intent.code"
                                                        >
                                                            <option
                                                                v-for="(intent, index) in questionIntents"
                                                                :key="index"
                                                            >
                                                                {{ intent.display_name }}
                                                            </option>
                                                        </select>
                                                        <select class="form-control" v-else v-model="intent.code">
                                                            <option
                                                                v-for="(entity, index) in questionEntities"
                                                                :key="index"
                                                            >
                                                                {{ entity }}
                                                            </option>
                                                        </select>
                                                    </div>
                                                    <div class="flex-grow-0">
                                                        <div
                                                            v-show="chatAgentDetailLoading"
                                                            class="spinner-border spinner-border-sm text-primary ml-2"
                                                            role="status"
                                                        >
                                                            <span class="sr-only">Loading...</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Parameter Value -->
                                        <div
                                            class="row mb-3"
                                            v-show="intent.detection_type == 'parameter' && questionEntitiesExist"
                                        >
                                            <div class="col-12">
                                                <span class="mt-1">Parameter Value Required</span>
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    maxlength="300"
                                                    v-model="intent.parameter_value_req"
                                                    placeholder="The value the learner needs to submit"
                                                />
                                                <p class="mb-0 mt-1 text-secondary small">
                                                    You can define acceptable variations by separating values with a
                                                    slash (i.e. medication/medications).
                                                </p>
                                            </div>
                                        </div>

                                        <!-- Intent Message -->
                                        <div class="row mb-3">
                                            <div class="col-12">
                                                <div class="mb-1">Display Label</div>
                                                <input
                                                    type="text"
                                                    class="form-control"
                                                    :class="{ 'border-danger': intent.isInvalid && !intent.message }"
                                                    maxlength="300"
                                                    v-model="intent.message"
                                                    placeholder="Text to display to the learner upon finding this intent"
                                                />
                                            </div>
                                        </div>

                                        <!-- Intent Correctness -->
                                        <div class="row mb-3">
                                            <div class="col-12 col-md-6">
                                                <label class="mb-1" for="correct">Correct/Incorrect</label>
                                                <select v-model="intent.correct" class="form-control">
                                                    <option :value="true">Correct</option>
                                                    <option :value="false">Incorrect</option>
                                                    <option :value="null">Neutral</option>
                                                </select>
                                            </div>
                                            <div class="col-12 col-md-6">
                                                <label class="mb-1" for="correct">Score</label>
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    class="form-control"
                                                    v-model="intent.score"
                                                />
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col-12">
                                                <div class="mb-1">Feedback Message</div>
                                                <textarea
                                                    type="text"
                                                    class="form-control"
                                                    maxlength="1000"
                                                    rows="2"
                                                    v-model="intent.feedback_message"
                                                    placeholder="A feedback message to display to the learner upon finding this intent"
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Create Intent -->
                        <div>
                            <a @click="createIntent(index + 1)" class="badge badge-light badge-pill">
                                <span class="icon icon-glyphicons-basic-plus mr-1"></span>
                                Key Information
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Empty Message -->
                <div v-show="intentListEmpty">
                    <div class="text-center">
                        <a @click="createIntent(0)" class="badge badge-light badge-pill mr-2">
                            <span class="icon icon-glyphicons-basic-plus mr-1"></span> Key Information
                        </a>
                    </div>
                </div>
            </div>

            <br />
        </template>
        <template slot="footer">
            <button @click="$emit('clone')" :disabled="caseLocked" type="button" class="btn btn-light">
                <span class="icon-glyphicons-basic-squares mr-1"></span>
                <span>Clone</span>
            </button>
            <button @click="openDelete()" :disabled="caseLocked" type="button" class="btn btn-light">
                <span class="icon icon-lg icon-glyphicons-basic-bin mr-1"></span>
                <span>Delete Content</span>
            </button>
            <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            <button @click="save()" :disabled="isLoading" class="btn btn-primary" type="button">Save</button>
        </template>
    </base-modal>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"

export default {
    name: "ChatPanelEditor",
    components: {
        BaseModal,
        BaseForm,
    },
    props: {
        savedPanel: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
        editPanelFormFields: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            panel: this.savedPanel,
            chatAgentsLoading: false,
            chatAgentDetailLoading: false,
            questionIntents: [],
            questionEntities: [],
        }
    },
    computed: {
        chatAgents() {
            return this.$store.getters.chatAgents
        },
        isLoading() {
            return this.loading || this.chatAgentsLoading
        },
        showIntentConfig() {
            return Boolean(this.panel.chat_agent_id)
        },
        selectedAgentId() {
            return this.panel.chat_agent_id
        },
        questionEntitiesExist() {
            return this.questionEntities && this.questionEntities.length > 0
        },
        chatAgentsManageRoute() {
            return this.$router.resolve({ name: "ChatAgents" }).href
        },
        intentListEmpty() {
            return this.panel.chat_intents && this.panel.chat_intents.filter((i) => !i.is_deleted).length === 0
        },
        caseLocked() {
            return this.$store.getters.caseIsReadOnly
        },
        chatPanelFormFields() {
            return [
                {
                    type: "divider",
                    name: "Behaviour",
                },
                {
                    name: "Reveal correctness of key information discovered",
                    key: "show_correctness",
                    type: "checkbox",
                    description: "Control whether learners see the correctness of their choices.",
                },
                {
                    name: "Allow learners to continue chatting when returning to this screen",
                    key: "allow_answer_changing",
                    type: "checkbox",
                },
                {
                    name: "Reveal missed information before moving on",
                    subtext: "Note: This option requires the Discovered Item List content-type to be present on this screen.",
                    key: "show_missed_items",
                    type: "checkbox",
                },
                {
                    type: "divider",
                    name: "Labels",
                },
                {
                    name: "Chat Input Placeholder",
                    key: "chat_input_placeholder",
                    placeholder: "E.g. Ask Jane a question here.",
                    type: "text",
                    maxlength: 200,
                },
                {
                    name: "Initial Chat Message",
                    key: "chat_initial_message",
                    placeholder: "E.g. Your questions will appear here.",
                    type: "text",
                    rows: 2,
                    maxlength: 250,
                },
                {
                    name: "Initial Chat Response",
                    key: "chat_initial_response",
                    placeholder: "E.g. Jane will respond here.",
                    type: "text",
                    rows: 2,
                    maxlength: 250,
                },
                {
                    type: "divider",
                    name: "Key Information",
                },
            ]
        },
    },
    watch: {
        savedPanel(newValue) {
            this.panel = newValue
        },
        selectedAgentId(newValue) {
            // Need this check for now - otherwise all panel changes trigger this
            // watcher.
            if (this.panel.panel_type == "chat") {
                this.fetchChatAgent(newValue)
            }
        },
    },
    methods: {
        // Open the editor modal.
        open() {
            this.panel = this.savedPanel

            // Fetch all chat agents.
            this.chatAgentsLoading = true
            this.$store.dispatch("fetchChatAgents").finally(() => {
                this.chatAgentsLoading = false
            })

            // Open the modal.
            $("#edit-chat-panel-modal").modal("show")

            if (this.selectedAgentId) {
                this.fetchChatAgent(this.selectedAgentId)
            }
        },
        // Fetch a chat agent.
        fetchChatAgent(id) {
            this.chatAgentDetailLoading = true
            let vm = this
            http.get("/api/chat-agents/" + id + "/")
                .then(function (response) {
                    vm.questionIntents = response.data.intents
                    vm.questionEntities = response.data.entities
                })
                .finally(() => (vm.chatAgentDetailLoading = false))
        },
        // Close the editor modal.
        close() {
            $("#edit-chat-panel-modal").modal("hide")
        },
        // Save the panel.
        save() {
            let vm = this
            let isValid = true
            this.panel.chat_intents
                .filter((intent) => !intent.is_deleted)
                .forEach((intent, index) => {
                    if (!intent.message) {
                        vm.$set(vm.panel.chat_intents[index], "isInvalid", true)
                        isValid = false
                    }
                })
            if (!isValid) {
                return
            }
            this.$emit("save", this.panel)
        },
        // Open the delete panel modal.
        openDelete() {
            this.$emit("open-delete")
        },
        // Add a new chat intent.
        createIntent(index) {
            let intent = {
                message: "",
                code: "",
                correct: true,
                detection_type: "intent",
            }
            this.panel.chat_intents.splice(index, 0, intent)
        },
        // Remove an intent (mark it as deleted).
        removeIntent(intent) {
            this.$set(intent, "is_deleted", true)
        },
        // Shift an intent up or down in the list.
        shiftIntentIndex(index, increment) {
            // Don't do anything if element is already at the start or end of the array.
            if ((index == 0 && increment == -1) || (index + 1 >= this.panel.chat_intents.length && increment == 1)) {
                return
            }
            // Remove the element from the array.
            let intent = this.panel.chat_intents.splice(index, 1)[0]
            // Put it back at the new position.
            this.panel.chat_intents.splice(index + increment, 0, intent)
        },
    },
}
</script>